import { Fragment, useState } from 'react';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { RelativeSpinner } from '@components/loading-spinner';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Stepper } from '@components/stepper';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { FieldTable, getFormEndpointVariables } from '../common';
import {
  useIntegrationWebhook,
  useSubscribeIntegrationWebhook,
  useUpdateIntegrationWebhook,
} from '../integrations-hooks';
import type {
  IntegrationEndpointVariable,
  IntegrationWebhook,
  IntegrationWebhookCreateRequestType,
} from '../integrations-types';

export const useSubscribeWebhookModal = () => useModal(SubscribeWebhook, { variant: 'LARGE' });

type StepType = 'types' | 'values' | 'results';

const SubscribeWebhook = ({ webhookId }: { webhookId: string }) => {
  const { t } = useScopedTranslate(locale);
  const [step, setStep] = useState<StepType>('types');
  const [response, setResponse] = useState<IntegrationWebhookCreateRequestType>();
  const webhookQuery = useIntegrationWebhook(webhookId);
  const webhook = webhookQuery.data;

  return (
    <Modal>
      <Modal.SimpleHeader title={t('subscribeWebhook')} />
      <Modal.Content>
        {!webhook || webhookQuery.isLoading ? (
          <RelativeSpinner />
        ) : (
          <>
            {step === 'types' && <FieldTypes setStep={setStep} webhook={webhook} />}
            {step === 'values' && (
              <ValuesTypes setStep={setStep} setResponse={setResponse} webhook={webhook} />
            )}
            {step === 'results' && <Results setStep={setStep} response={response} />}
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

const WebhookStepper = ({ currentIndex }: { currentIndex: number }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stepper
      items={[{ label: t('matchTypes') }, { label: t('addValue') }, { label: t('subscribe') }]}
      activeIndex={currentIndex}
      showCompleted
    />
  );
};

type FieldTypesData = {};
const FieldTypesForm = createForm<FieldTypesData>();

const FieldTypes = ({
  webhook,
  setStep,
}: {
  webhook: IntegrationWebhook;
  setStep: (value: StepType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const variables = webhook.variables as IntegrationEndpointVariable[];
  const updateMutation = useUpdateIntegrationWebhook();

  const formValues = formStore.useFormValues();
  const containsUrl = Object.values(formValues).some((value) => value === 'webhookUrl');
  const visibleColumns = {
    name: true,
    type: true,
    label: false,
    example: false,
    default: false,
    visible: false,
    required: false,
    format: false,
  };

  const handleUpdateVariables = async () => {
    const newVariables = getFormEndpointVariables({
      formStore,
      endpointVariables: variables,
      visibleColumns,
    });
    await updateMutation.mutateAsync({
      shortId: webhook.shortId,
      data: {
        variables: newVariables,
      },
    });
    setStep('values');
  };

  return (
    <Stack space={6}>
      <WebhookStepper currentIndex={0} />
      <FieldTypesForm store={formStore} onSubmit={handleUpdateVariables}>
        {!containsUrl && (
          <Alert variant="error">
            <Text text={t('mustInludeUrlError')} color="elementRed100" font="regular14" />
          </Alert>
        )}
        <FieldTable
          endpointId={webhook.shortId}
          form={FieldTypesForm}
          endpointVariables={variables}
          showWebhookUrl
          showValueType={false}
          visibleColumns={visibleColumns}
        />
        <FieldTypesForm.Actions>
          <Stack orientation="horizontal" space={2} align="center">
            <FieldTypesForm.Reset onClick={hideModal} text={t('core.close')} variant="secondary" />
            <FieldTypesForm.Submit
              stopPropagation
              isDisabled={updateMutation.isLoading || !containsUrl}
              text={t('core.continue')}
              variant="primary-blue"
            />
          </Stack>
        </FieldTypesForm.Actions>
      </FieldTypesForm>
    </Stack>
  );
};

type ValuesData = {};
const ValuesForm = createForm<ValuesData>();

const ValuesTypes = ({
  webhook,
  setStep,
  setResponse,
}: {
  webhook: IntegrationWebhook;
  setStep: (value: StepType) => void;
  setResponse: (value: IntegrationWebhookCreateRequestType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const variables = webhook.variables as IntegrationEndpointVariable[];
  const subscribeMutation = useSubscribeIntegrationWebhook(webhook.shortId);

  const handleUpdateVariables = async (data: ValuesData) => {
    const processedData = { ...data };
    for (const variable of variables) {
      if (variable.type === 'array_of_strings' && variable.name in processedData) {
        const value = processedData[variable.name as keyof ValuesData] as string;
        (processedData as Record<string, unknown>)[variable.name] = value
          .split('\n')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
    }
    const response = await subscribeMutation.mutateAsync(processedData);
    setResponse(response);
    setStep('results');
  };

  return (
    <Stack space={6}>
      <WebhookStepper currentIndex={1} />
      <ValuesForm store={formStore} onSubmit={handleUpdateVariables}>
        {variables.map((field) => (
          <Fragment key={field.name}>
            <ValueField
              id={field.name as keyof ValuesData}
              field={field}
              form={ValuesForm}
              uniqueUrl={webhook.uniqueUrl}
            />
          </Fragment>
        ))}
        <ValuesForm.Actions>
          <Stack orientation="horizontal" space={2} align="center">
            <ValuesForm.Reset
              stopPropagation
              text={t('core.back')}
              variant="secondary"
              onClick={() => setStep('types')}
            />
            <ValuesForm.Submit
              text={t('subscribe')}
              variant="primary-blue"
              disabled={subscribeMutation.isLoading}
            />
          </Stack>
        </ValuesForm.Actions>
      </ValuesForm>
    </Stack>
  );
};

type ValueFieldProps = {
  field: Pick<IntegrationEndpointVariable, 'name' | 'example' | 'type'>;
  form: typeof ValuesForm;
  id: keyof ValuesData;
  uniqueUrl: string;
};

export const ValueField = ({ field, form: Form, id, uniqueUrl }: ValueFieldProps) => {
  const { t } = useScopedTranslate(locale);
  if (field.type === 'array') {
    return <TextField label={field.name} isDisabled />;
  }
  if (field.type === 'array_of_strings') {
    const defaultValue = Array.isArray(field.example) ? field.example.join('\n') : '';
    return (
      <Form.Textarea
        id={id}
        label={t('arrayOfStringsLabel', { fieldName: field.name })}
        defaultValue={defaultValue}
        rows={4}
      />
    );
  }
  if (field.type === 'boolean') {
    return (
      <Form.Switch id={id} label={field.name} defaultValue={field.example as boolean | undefined} />
    );
  }
  if (field.type === 'webhookUrl') {
    return (
      <Form.TextField
        id={id}
        label={field.name}
        defaultValue={uniqueUrl}
        isDisabled
        key={uniqueUrl}
      />
    );
  }
  return (
    <Form.TextField id={id} label={field.name} defaultValue={field.example as string | undefined} />
  );
};

const Results = ({ response, setStep }: { response?: any; setStep: (value: StepType) => void }) => {
  const { t } = useScopedTranslate(locale);
  const isSuccess = response?.success ?? false;
  const color = isSuccess ? 'elementGreen200' : 'elementRed100';

  if (!response) {
    return <RelativeSpinner />;
  }

  return (
    <>
      <Stack space={6}>
        <WebhookStepper currentIndex={2} />
        <Card space={4}>
          <Stack space={4}>
            <Stack space={2} orientation="horizontal" justify="space-between">
              <Text
                font="semibold14"
                color={color}
                text={isSuccess ? t('successfullySubscribed') : t('unsuccessfullySubscribed')}
              />
              <Text font="semibold14" color={color} text={response.statusCode.toString()} />
            </Stack>
            <CodeBlock header={t('response')} data={response.responseData ?? {}} />
          </Stack>
        </Card>
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
          {!isSuccess && (
            <Button
              text={t('core.back')}
              variant="primary-blue"
              onClick={() => setStep('values')}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

const locale = {
  en: {
    subscribeWebhook: 'Subscribe to a webhook',
    matchTypes: 'Select field types',
    addValue: 'Add value',
    mustInludeUrlError:
      'In order to subscribe to a webhook one of the request values here below must be of the type "Unique webhook url"',
    subscribe: 'Subscribe',
    successfullySubscribed: 'Successfully subscribed to a webhook',
    unsuccessfullySubscribed: 'Could not subscribe to a webhook',
    response: 'Response',
    arrayOfStringsLabel: '{fieldName} (one item per line)',
  },
};
